import { useContext } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { AppBar, Button, Grid, Toolbar, Typography, useTheme } from '@mui/material'

import meshifyProtectLogo from '../../images/meshify-logo.svg'
import meshifyProtectShield from '../../images/protect-icon-logo.svg'
import { AuthenticationContext } from '../../providers/auth-provider'
import { MESHIFYID_TOKEN, SIDEWALKID_TOKEN } from '../../utils/constants'
import { fixQueryString } from '../../utils/helpers'
import useIsMobile from '../../utils/hooks/useIsMobile'
import useScrollToTop from '../../utils/hooks/useScrollToTop'
import ErrorBoundary from '../error-boundary'

import useStyles from './index.styles'

const PublicRouteWrapper = ({
  variant = 'default',
  routeType,
}: {
  variant?: 'default' | 'alert-acknowledgement'
  routeType: 'unauthenticated' | 'authenticated'
}) => {
  const classes = useStyles
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useIsMobile()
  useScrollToTop()

  const queryString = fixQueryString(location.pathname)
  const { isAuthenticated } = useContext(AuthenticationContext)

  const { alertAckData, alertAckResponse, alertAckCode } = location.state || {}

  const loggedOut = localStorage.getItem('logged-out') === 'true'
  const meshifyId = localStorage.getItem(MESHIFYID_TOKEN)
  const sidewalkId = localStorage.getItem(SIDEWALKID_TOKEN)

  if (routeType === 'authenticated') {
    if (!isAuthenticated) return <Navigate to="/login" replace state={loggedOut ? {} : { from: location }} />
  } else if (routeType === 'unauthenticated') {
    if (alertAckData && alertAckResponse && alertAckCode && isAuthenticated) {
      return <Navigate to={`/submit?type=${alertAckResponse}`} state={{ data: alertAckData, code: alertAckCode, fromLogin: true }} replace />
    }
    if (isAuthenticated) {
      if (meshifyId && sidewalkId) return <Navigate to={`/sensor-activation${queryString}`} replace />
      return <Navigate to="/dashboard" replace />
    }
  }

  return (
    <ErrorBoundary>
      <Grid container sx={classes.background(theme, variant)}>
        {/* Centered white card */}
        <Grid container={true} sx={classes.cardContainer(theme, variant)}>
          {variant === 'default' && (
            <AppBar position="static" elevation={0} sx={classes.topAppBar(theme)}>
              <Toolbar sx={classes.topToolbar}>
                <img src={meshifyProtectLogo} alt="Protect Mobile Logo" width="196" height="54" />
              </Toolbar>
            </AppBar>
          )}

          {/* Main content */}
          <Grid sx={classes.contentContainer(variant)}>
            <Grid container={true} sx={classes.contentInner(variant)}>
              <Outlet />
            </Grid>
          </Grid>

          {variant === 'default' && (
            <Grid component="footer" container={true} sx={classes.promoFooter}>
              <Grid container={true} sx={classes.promoFooterContent(isMobile)}>
                <Grid container={true} sx={classes.promoFooterLeft}>
                  <Grid sx={classes.promoFooterIconWrap(isMobile)}>
                    <img src={meshifyProtectShield} alt="Protect Mobile Logo" width="56" height="56" />
                  </Grid>
                  <Grid>
                    <Typography variant={isMobile ? 'body1' : 'h6'}>Protect Mobile App</Typography>
                    <Typography variant={isMobile ? 'body2' : 'body1'}>More features, on the go</Typography>
                  </Grid>
                </Grid>
                <Button variant="text" size={isMobile ? 'small' : 'medium'} LinkComponent="a" href="https://meshifyprotect.app.link/download-app">
                  Download Now
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </ErrorBoundary>
  )
}

export default PublicRouteWrapper
